import { Colaboracao, FocoNoCliente, Inovacao, MandouBem, Resolutividade, Lideranca, Uau, Valeu } from 'components/icons';
import { differenceInDays, format } from 'date-fns';
import InputMask from 'react-input-mask';

import { UserAvatar } from 'components/user-avatar';
import { useAuth } from 'context/AuthContext';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { BiInfoCircle } from 'react-icons/bi';
import { FaHeart } from 'react-icons/fa';
import { MdModeComment } from 'react-icons/md';
import { registerInteraction } from 'services/interactions.service';
import { useModal } from 'context/ModalContext';
import { getKPIHistoryPag, getServerTime } from 'services/dashboard.service';
import Box from 'components/ui/box';
import ArticlePlaceholder from 'components/ui/article-placeholder';
import Button from 'components/ui/button';
import { TbMessageCancel, TbSearch } from 'react-icons/tb';
import { convertEmojis, itemsPerPageExtrato } from 'utils/helpers';
import Humanograma from 'components/Humanograma';
import { FiEdit3 } from 'react-icons/fi';
import { BsArrowReturnLeft } from 'react-icons/bs';
import ModalCancelRecognition from 'components/modal-cancel-recognition';
import ModalComment from 'components/modal-comment';
import ModalAcessivel from 'components/modal-acessivel';

const iconSize = 16;
const Coins: any = {
  uau: (
    <div className="w-[16px]">
      <Uau size={iconSize} />
    </div>
  ),
  mandou: (
    <div className="w-[16px]">
      <MandouBem size={iconSize} />
    </div>
  ),
  valeu: (
    <div className="w-[16px]">
      <Valeu size={iconSize} />
    </div>
  ),
};

const CoinLabel: any = {
  uau: '#UAU',
  mandou: '#MANDOUBEM',
  valeu: '#VALEU',
};

const Behaviors: any = {
  colaboracao: (
    <div className="w-[16px]">
      <Colaboracao size={iconSize} />
    </div>
  ),
  inovacao: (
    <div className="w-[16px]">
      <Inovacao size={iconSize} />
    </div>
  ),
  foco: (
    <div className="w-[16px]">
      <FocoNoCliente size={iconSize} />
    </div>
  ),
  resolutividade: (
    <div className="w-[16px]">
      <Resolutividade size={iconSize} />
    </div>
  ),
  lideranca: (
    <div className="w-[16px]">
      <Lideranca size={iconSize} />
    </div>
  ),
};

const BehaviorLabel: any = {
  colaboracao: 'Colaboração',
  inovacao: 'Inovação',
  foco: 'Foco no Cliente',
  resolutividade: 'Protagonismo',
  lideranca: 'Liderança Engajadora',
};

interface Props {
  filter?: any;
}

export default function RecognitionReceived(data: Props) {
  const { filter } = data;
  const { user, currentCycle } = useAuth();
  const [list, setList] = useState<any[]>([]);
  const { showModal } = useModal();
  const [loadingList, setLoadingList] = useState(true);
  const [total, setTotal] = useState(0);
  const [allLoaded, setAllLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [time, setTime] = useState<number>();
  const [returnRecognitionId, setReturnRecognitionId] = useState<string | undefined>();

  const [modalCommentObj, setModalCommentObj] = useState<any>();
  const [loadingInteraction, setLoadingInteraction] = useState<boolean | undefined>(false);

  // Ref para controlar se o carregamento inicial já foi feito
  const initialLoadDone = useRef(false);
  // Ref para o input de busca
  const searchInputRef = useRef<any>(null);

  // Carrega o horário do servidor
  useEffect(() => {
    async function loadTime() {
      const res = await getServerTime();
      setTime(res);
    }

    if (user && currentCycle) {
      loadTime();
    }
  }, [currentCycle, user]);

  const hasLike = useCallback(
    (item: any) => {
      return !!item?.interaction?.find((i: any) => i.sender === user?._id && !!i.like);
    },
    [user?._id],
  );

  function canEdit(item: any) {
    if (item && time) {
      const differenceTime = differenceInDays(new Date(time), new Date(item.created));
      return differenceTime <= 10;
    }
    return false;
  }

  const sendInteraction = useCallback(
    async (item: any, type: string, message?: string) => {
      setLoadingInteraction(true);
      const index = list?.findIndex((i: any) => i._id === item._id);

      let payload: any;
      const newList = [...list];
      const interactionItem = item.interaction[0];

      if (!item?.interaction?.length && item?.action_id) {
        payload = {
          item: item.action_id as string,
          player: item.sender as string,
          sender: user?._id as string,
          senderName: user?.name as string,
          like: type === 'like' && hasLike(item) ? false : type === 'like' && !hasLike(item) ? true : hasLike(item),
          message: type === 'message' && typeof message === 'string' && message.trim().length ? message.trim() : undefined,
          created: {
            $date: new Date().getTime(),
          },
        };
      } else if (item?.interaction?.length && item?.action_id && interactionItem?._id) {
        if (type === 'like') {
          payload = {
            ...interactionItem,
            like: !hasLike(item),
            updated: {
              $date: new Date().getTime(),
            },
          };
        } else if (type === 'message') {
          payload = {
            ...interactionItem,
            message,
            updated: {
              $date: new Date().getTime(),
            },
          };
        }

        if (typeof payload?.created === 'number') {
          payload.created = {
            $date: payload.created,
          };
        }
      }

      if (payload && payload.created) {
        const data = await registerInteraction(payload);

        if (newList[index]?.interaction?.length) {
          newList[index].interaction[0] = data;
        } else {
          newList[index].interaction = [data];
        }

        setList(newList);
      }

      setLoadingInteraction(false);
    },
    [hasLike, list, user?._id, user?.name],
  );

  function getComment(item: any) {
    const interaction = item.interaction?.find((e: any) => !!e.message?.trim().length);
    return interaction || null;
  }

  function handleDetail(item: any) {
    showModal('extrato-detalhe', item);
  }

  function getReturnComment(item: any) {
    if (item?.cancelled && item?.returnRecognition?.message) {
      return item?.returnRecognition?.message;
    }
    return null;
  }

  const isOldVersion = (item: any): boolean => {
    return !!item.proximidade && !!item.hierarquia && !!item.total;
  };

  // Função para carregar dados - recebe parâmetros explícitos para evitar problemas de timing
  const fetchData = useCallback(
    async (searchTerm: string, loadMore: boolean, currentList: any[]) => {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoadingList(true);
      }

      try {
        // Monta o filtro com base nos parâmetros
        const filterPayload: any = {
          ...filter,
          cycleId: filter?.cycleId || currentCycle?._id,
        };

        if (searchTerm.trim()) {
          filterPayload.search = searchTerm.trim();
        }

        // Se for "carregar mais", busca o restante (10000 para pegar tudo)
        // Se for primeira carga, busca apenas itemsPerPageExtrato
        const rangeStart = loadMore ? currentList.length : 0;
        const rangeTotal = loadMore ? 10000 : itemsPerPageExtrato;

        const response = await getKPIHistoryPag(
          user?._id,
          filterPayload,
          rangeStart,
          rangeTotal,
          currentCycle?.date_start,
          currentCycle?.date_finish,
        );

        if (loadMore) {
          // Concatena com a lista existente
          setList([...currentList, ...response.result]);
          setAllLoaded(true); // Após carregar mais, já carregou tudo
        } else {
          // Primeira carga ou nova busca: substitui a lista
          setList(response.result);
          // Se o total for maior que o que carregamos, há mais itens
          setAllLoaded(response.range.total <= itemsPerPageExtrato);
        }

        setTotal(response.range.total);
      } catch (error) {
        console.error('Erro ao carregar reconhecimentos:', error);
      } finally {
        setLoadingList(false);
        setLoadingMore(false);
      }
    },
    [currentCycle?._id, currentCycle?.date_finish, currentCycle?.date_start, filter, user?._id],
  );

  // Carregamento inicial - só executa uma vez quando as dependências estão prontas
  useEffect(() => {
    if (user && currentCycle && filter?.cycleId && !initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchData('', false, []);
    }
  }, [user, currentCycle, filter?.cycleId, fetchData]);

  // Handler para busca
  const onSearch = (e: FormEvent) => {
    e.preventDefault();

    const inputValue = searchInputRef.current?.value || '';
    setSearchValue(inputValue);
    setAllLoaded(false);

    // Chama fetchData com o valor atual do input
    fetchData(inputValue, false, []);

    // Remove o foco do input
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  // Handler para carregar mais
  const handleLoadMore = () => {
    if (loadingMore || allLoaded) return;
    fetchData(searchValue, true, list);
  };

  // Handler para o callback do modal de cancelamento
  const handleCancelCallback = (action: string) => {
    if (action === 'remove') {
      // Reseta e recarrega
      setAllLoaded(false);
      setSearchValue('');
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
      fetchData('', false, []);
    } else if (action === 'close') {
      setReturnRecognitionId(undefined);
    }
  };

  // Determina se deve mostrar o botão "Carregar mais"
  const showLoadMoreButton = !allLoaded && total > itemsPerPageExtrato && list.length < total;

  return (
    <>
      <div className="flex flex-col items-start justify-start lg:flex-row lg:justify-between lg:items-center mb-8">
        <h3 className="text-white font-bold text-xl mb-4 lg:mb-0" tabIndex={0}>
          Reconhecimentos recebidos
          <span className="text-xl lg:text-2xl font-bold text-yellow ">{total ? ` (${total})` : null}</span>
        </h3>
        <div className="flex flex-row items-center space-x-4">
          <form onSubmit={onSearch} className="pl-1 -ml-1 flex-1 flex space-x-5">
            <InputMask
              placeholder="Pesquise com nome, matrícula, prefixo ou grupo"
              alwaysShowMask={false}
              mask={''}
              ref={(ref: any) => (searchInputRef.current = ref)}
              inputMode="search"
              style={{ fontSize: 16 }}
              className="min-w-full lg:min-w-[416px] bg-maastricht-blue text-sm text-white font-thin flex-1 rounded-md py-2 px-4"
            />
            <button type="submit" aria-label="Pesquisar">
              <TbSearch size={28} className="cursor-pointer hover:ring-2 mr-1 rounded-full" />
            </button>
          </form>
        </div>
      </div>

      <Box className="relative bg-oxford-blue border-electric-blue lg:p-6 flex flex-col justify-center items-center border-opacity-100 bg-opacity-100">
        {loadingList && (
          <div className="space-y-2 w-full">
            <ArticlePlaceholder />
            <ArticlePlaceholder />
          </div>
        )}
        {!loadingList && (
          <div className="space-y-2 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {!!list?.length &&
                list?.map((item: any) => (
                  <div className="bg-maastricht-blue-3 rounded-xl text-gray-300 space-y-2 py-6 px-6 lg:px-8" key={item?._id}>
                    <div className="flex flex-col lg:flex-col items-start lg:items-start mb-5">
                      <div className="flex-1 flex flex-col-reverse lg:flex-row items-start space-x-2 leading-tight w-full">
                        <div className="flex flex-1 w-full items-start">
                          <div className="mr-2 w-11">
                            <UserAvatar data={item.sender_avatar} />
                          </div>
                          <div className="flex-1 w-full block">
                            <Humanograma
                              data={{
                                ...item,
                                nome: item.sender_name,
                                funcao: item.sender_funcao,
                                _id: item.sender,
                                prefixo: item.sender_prefixo,
                                nome_dependencia: item.sender_nome_dependencia,
                              }}
                            >
                              <div className="flex items-center">
                                <p className="flex flex-row items-center text-sm lg:text-base text-alice-blue font-semibold hover:cursor-pointer hover:underline">
                                  {item.sender_nome_guerra || item.sender_name.split(' ')[0]}{' '}
                                  <span className="font-thin ml-1">({item.sender})</span>
                                  <BiInfoCircle className="hidden lg:block relative ml-1" />
                                </p>
                                {!item.cancelled && (
                                  <>
                                    <span className="hidden lg:block whitespace-nowrap font-thin text-sm lg:text-base text-alice-blue ml-1">
                                      enviou:
                                    </span>
                                    <span className="lg:hidden font-thin text-sm lg:text-base text-alice-blue ml-1">enviou:</span>
                                  </>
                                )}
                              </div>
                            </Humanograma>

                            <div className="block 2xl:flex flex-col 2xl:flex-row 2xl:items-center w-full space-y-2 2xl:space-y-0 2xl:space-x-2 mt-1">
                              {!!item.cancelled ? (
                                <div className="inline-flex flex-row items-center space-x-2 px-3 py-1 text-[#ED2B48] bg-oxford-blue rounded-full">
                                  <TbMessageCancel size={16} />
                                  <span className="text-xs whitespace-nowrap">Reconhecimento cancelado pelo autor</span>
                                </div>
                              ) : (
                                <>
                                  <div
                                    className="inline-flex flex-row items-center space-x-2 px-3 py-1 text-yellow bg-oxford-blue rounded-full"
                                    tabIndex={0}
                                  >
                                    {Coins[item.coin]} <span className="text-xs">{CoinLabel[item.coin]}</span>
                                  </div>
                                  <br className="block 2xl:hidden" />
                                  <div
                                    className={`inline-flex flex-row items-center space-x-2 px-3 py-1 text-yellow bg-oxford-blue rounded-full ${
                                      item.behavior === 'foco' ? 'min-w-[142px]' : ''
                                    }`}
                                    tabIndex={0}
                                  >
                                    {Behaviors[item.behavior]} <span className="text-xs">{BehaviorLabel[item.behavior]}</span>
                                  </div>
                                  {!!item.total && (
                                    <>
                                      <br className="block 2xl:hidden" />
                                      <div className="inline-flex items-center space-x-2">
                                        <div
                                          onClick={() => (isOldVersion(item) ? handleDetail(item) : null)}
                                          className={`ring-white whitespace-nowrap 2xl:mt-0 flex-1 2xl:flex-none inline-flex flex-row items-center space-x-2 px-2 bg-oxford-blue py-1 text-electric-blue rounded-full ${
                                            isOldVersion(item) ? 'cursor-pointer hover:ring-2' : ''
                                          }`}
                                          tabIndex={0}
                                          aria-label={`total = ${item.total} pontos`}
                                        >
                                          <span>total = {item.total} pontos</span>{' '}
                                        </div>

                                        {isOldVersion(item) ? (
                                          <div>
                                            <BiInfoCircle size={20} />
                                          </div>
                                        ) : null}
                                      </div>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="text-gray-400 text-xs leading-none flex justify-end w-full my-3 lg:my-0">
                          <span>{format(new Date(item.time), 'dd MMM yyyy | HH:mm')}</span>
                        </div>
                      </div>
                    </div>

                    <hr className="border-prussian-blue" />

                    {!!item.message && (
                      <>
                        <div className="p-2 flex-1 flex-col" tabIndex={0}>
                          <p className="text-electric-blue font-thin">Reconhecimento recebido:</p>
                          <div className="text-alice-blue" dangerouslySetInnerHTML={{ __html: convertEmojis(item.message) }} />
                        </div>

                        {!!getComment(item) && (
                          <div className="p-2 flex-1 flex-col">
                            <div className="flex justify-between">
                              <p className="text-electric-blue font-thin">Comentário:</p>
                              {canEdit(getComment(item)) && (
                                <button
                                  onClick={() => {
                                    if (loadingInteraction) return;

                                    setModalCommentObj({
                                      ...item,
                                      message: getComment(item).message,
                                    });
                                  }}
                                  className={`flex items-center cursor-pointer text-electric-blue ${
                                    loadingInteraction ? '!cursor-wait' : ''
                                  }`}
                                  disabled={loadingInteraction}
                                >
                                  <FiEdit3 size={20} className="mr-2" /> Editar
                                </button>
                              )}
                            </div>
                            <div
                              className="text-alice-blue"
                              dangerouslySetInnerHTML={{
                                __html: convertEmojis(getComment(item).message),
                              }}
                            ></div>
                          </div>
                        )}
                      </>
                    )}

                    {!!getReturnComment(item) && (
                      <div className="p-2 flex-1 flex-col">
                        <p className="text-electric-blue font-thin">Motivo do cancelamento:</p>
                        <div
                          className="text-alice-blue"
                          dangerouslySetInnerHTML={{
                            __html: convertEmojis(getReturnComment(item)),
                          }}
                        ></div>
                      </div>
                    )}

                    {!item.cancelled && (
                      <div className="flex justify-end space-x-6">
                        {!!item.canReverseRecognition && (
                          <button
                            className={`flex items-center uppercase text-[#ED2B48]`}
                            onClick={() => setReturnRecognitionId(item.action_id)}
                          >
                            <BsArrowReturnLeft size={20} className="mr-2" />
                            Devolver para remetente
                          </button>
                        )}

                        <button
                          className={`flex items-center uppercase ${hasLike(item) ? 'text-electric-blue' : 'text-grey-dark'} ${
                            loadingInteraction ? '!cursor-wait' : ''
                          }`}
                          onClick={() => {
                            if (loadingInteraction || user?.extra?.recognition === false) return;
                            sendInteraction(item, 'like');
                          }}
                          disabled={loadingInteraction}
                        >
                          <FaHeart size={20} className="mr-2" />
                          {hasLike(item) ? 'Curtido' : 'Curtir'}
                        </button>

                        {!getComment(item) && (
                          <button
                            className={`flex items-center uppercase text-grey-dark ${loadingInteraction ? '!cursor-wait' : ''}`}
                            onClick={() => {
                              if (loadingInteraction || user?.extra?.recognition === false) return;
                              setModalCommentObj({ ...item, message: '' });
                            }}
                            disabled={loadingInteraction}
                          >
                            <MdModeComment size={20} className="mr-2" />
                            Comentar
                          </button>
                        )}
                      </div>
                    )}

                    {!!item.systemMessage && (
                      <>
                        <hr className="border-prussian-blue !mt-4" />
                        <div className="p-2 flex-1 flex-col">
                          <div
                            className="text-alice-blue text-sm font-light italic text-gray-400"
                            dangerouslySetInnerHTML={{
                              __html: item.systemMessage,
                            }}
                          ></div>
                        </div>
                      </>
                    )}
                  </div>
                ))}

              {!list?.length && (
                <>
                  <p className="text-white text-base text-center font-light leading-none block py-6">
                    Nenhum reconhecimento recebido até o momento.
                  </p>
                </>
              )}
            </div>

            {showLoadMoreButton && (
              <div className="w-full flex justify-center mt-8">
                <Button
                  disabled={loadingMore}
                  className="px-4 py-2 mx-auto"
                  type="button"
                  onClick={handleLoadMore}
                >
                  {loadingMore ? 'Carregando...' : 'Carregar mais'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Box>

      {!!returnRecognitionId && (
        <ModalCancelRecognition
          callback={handleCancelCallback}
          id={returnRecognitionId}
          type="return"
        />
      )}

      {!!modalCommentObj?._id && (
        <ModalAcessivel onRequestClose={() => setModalCommentObj(undefined)} isOpen={modalCommentObj}>
          <ModalComment
            close={() => setModalCommentObj(undefined)}
            message={modalCommentObj.message}
            sendMessage={(message, item) => {
              sendInteraction(item, 'message', message);
              setModalCommentObj(undefined);
            }}
            modalCommentObj={modalCommentObj}
          />
        </ModalAcessivel>
      )}
    </>
  );
}
